import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StorageServerStatsDto } from "@/types/admin-storage";
import { formatBytes } from "@/utils/formatBytes";

interface StorageServersSectionProps {
  servers: StorageServerStatsDto[];
}

const formatUptime = (seconds: number | undefined): string => {
  if (seconds === undefined) return "—";
  const days = Math.floor(seconds / (24 * 3600));
  const hours = Math.floor((seconds % (24 * 3600)) / 3600);
  if (days > 0) return `${days}d ${hours}h`;
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

export const StorageServersSection: React.FC<StorageServersSectionProps> = ({
  servers,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  if (servers.length === 0) {
    return null;
  }

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">
          {t("storage.region.servers.sectionTitle")}
        </h2>
        <Button
          type="button"
          variant="new_ghost"
          size="sm"
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? (
            <ChevronDown className="size-4" />
          ) : (
            <ChevronRight className="size-4" />
          )}
          {expanded
            ? t("storage.region.servers.collapse")
            : t("storage.region.servers.expand")}
        </Button>
      </div>
      {expanded ? (
        <div className="flex flex-col gap-4">
          {servers.map((server) => (
            <div
              key={server.endpoint}
              className="rounded-md border border-base-border p-4 flex flex-col gap-3"
            >
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-muted-foreground">
                <span>
                  <strong className="text-foreground">
                    {t("storage.region.servers.endpoint")}:
                  </strong>{" "}
                  <span className="font-mono">{server.endpoint}</span>
                </span>
                <span>
                  <strong className="text-foreground">
                    {t("storage.region.servers.state")}:
                  </strong>{" "}
                  {server.state}
                </span>
                <span>
                  <strong className="text-foreground">
                    {t("storage.region.servers.uptime")}:
                  </strong>{" "}
                  {formatUptime(server.uptimeSeconds)}
                </span>
                <span>
                  <strong className="text-foreground">
                    {t("storage.region.servers.version")}:
                  </strong>{" "}
                  {server.version ?? "—"}
                </span>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="h-9">
                      <TableHead>
                        {t("storage.region.servers.drive.endpoint")}
                      </TableHead>
                      <TableHead>
                        {t("storage.region.servers.drive.state")}
                      </TableHead>
                      <TableHead>
                        {t("storage.region.servers.drive.total")}
                      </TableHead>
                      <TableHead>
                        {t("storage.region.servers.drive.used")}
                      </TableHead>
                      <TableHead>
                        {t("storage.region.servers.drive.available")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {server.drives.map((drive, index) => (
                      <TableRow key={`${drive.endpoint}-${index}`}>
                        <TableCell className="font-mono text-xs">
                          {drive.endpoint}
                        </TableCell>
                        <TableCell className="text-xs">{drive.state}</TableCell>
                        <TableCell>{formatBytes(drive.totalBytes)}</TableCell>
                        <TableCell>{formatBytes(drive.usedBytes)}</TableCell>
                        <TableCell>
                          {formatBytes(drive.availableBytes)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
};
